import React, { useContext } from "react";
import { personBeskikkelserForm } from "../../../../../config/form/person/stamoplysninger/personBeskikkelseFormConfig";
import { defaultPersonBeskikkelserColumns } from "../../../../../config/person";
import {
  createColumnEditorTrigger,
  getHeaderActiveColumns,
  TD,
} from "../../../../../util/table";
import { tableToXLSX } from "../../../../../util/tableExportUtils";
import ButtonDownload from "../../../../ButtonDownload";
import ActiveIcon from "../../../../icons/ActiveIcon";
import { ModalProvider } from "../../../../Modal/Modal";
import {
  TableBody,
  TableHead,
  TableRoot,
  TableRow,
} from "../../../../Table/TableBasic";
import ColumnEditorModal from "@/components/column-editor/ColumnEditorModal";
import IngenRegistreringer from "../IngenRegistreringer";
import { PersonDto } from "../../../../../interfaces/schema";
import { PersonBeskikkelserField } from "../../../../../interfaces/personer";
import { SortBy, IColumn } from "../../../../../interfaces/table";
import useLocalStorageState from "use-local-storage-state";
import { UserQueryContext } from "../../../../../context/UserContext";
import { periodeIsActive } from "../../../../../util/date";
import { orderBy } from "../../../../../util/orderBy";

const defaultSortBy: SortBy[] = [
  {
    direction: "desc",
    field: "periode.tilDatoInklusiv",
  },
  {
    direction: "desc",
    field: "periode.fraDato",
  },
];

export default function Beskikkelser(props: {
  person: PersonDto;
  personBeskikkelserColumns: IColumn<PersonBeskikkelserField>[];
  setPersonBeskikkelserActiveColumns: React.Dispatch<
    React.SetStateAction<PersonBeskikkelserField[]>
  >;
  refetch: InvalidateDataFunction<any>;
}) {
  const { user, hasPermission } = useContext(UserQueryContext);
  const isAuth = hasPermission(user, "BasisWrite");
  const {
    person,
    personBeskikkelserColumns,
    setPersonBeskikkelserActiveColumns,
    refetch,
  } = props;
  const { personBeskikkelse } = person;
  const [sortBy, setSortBy] = useLocalStorageState<SortBy[]>(
    "personBeskikkelserSortBy",
    {
      defaultValue: defaultSortBy,
    },
  );

  if (personBeskikkelse?.length === 0) return <IngenRegistreringer />;

  // event handlers
  function downloadHandler() {
    tableToXLSX(personBeskikkelserColumns, personBeskikkelse ?? [], {
      fileName: `beskikkelser-for-${person.kundeNr}`,
      additionalData: {
        kundenr: person.kundeNr,
      },
    });
  }

  return (
    <>
      <ButtonDownload onClick={downloadHandler} />
      <TableRoot>
        <ModalProvider>
          <TableHead ColumnEditorTrigger={createColumnEditorTrigger()}>
            <>
              <th className="indicator"></th>
              {getHeaderActiveColumns(personBeskikkelserColumns, {
                defaultSortBy,
                setSortBy,
                sortBy,
              })}
            </>
          </TableHead>
          <ColumnEditorModal
            defaultColumns={defaultPersonBeskikkelserColumns}
            columns={personBeskikkelserColumns}
            setActiveColumns={setPersonBeskikkelserActiveColumns}
          />
        </ModalProvider>
        <TableBody>
          {orderBy(
            personBeskikkelse,
            [
              ...sortBy.map((s) => s.field),
              ...defaultSortBy.map((d) => d.field),
            ],
            [
              ...sortBy.map((s) => s.direction),
              ...defaultSortBy.map((d) => d.direction),
            ],
          ).map((beskikkelse) => {
            return (
              <TableRow
                key={beskikkelse.id}
                editConfig={
                  isAuth
                    ? personBeskikkelserForm(beskikkelse.id, person.id)
                    : undefined
                }
                invalidateData={refetch}
              >
                <td
                  className={`indicator${
                    periodeIsActive(
                      new Date(beskikkelse.periode.fraDato),
                      new Date(beskikkelse.periode.tilDatoInklusiv),
                    )
                      ? " has-effect"
                      : ""
                  }`}
                >
                  {/* Add class 'has-effect' on indicator active row */}
                  <ActiveIcon />
                </td>
                <>
                  {personBeskikkelserColumns
                    .filter((col) => col.active)
                    .map((col) => {
                      return (
                        <TD key={col.field} col={col} rowData={beskikkelse} />
                      );
                    })}
                </>
              </TableRow>
            );
          })}
        </TableBody>
      </TableRoot>
    </>
  );
}
